import React from "react";
import {
  DndContext,
  DragOverlay,
  closestCenter,
} from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { Typography } from "../../../common";
import MarkdownWrapper from "../../../common/MarkdownWrapper";
import { GripVertical, Box } from "lucide-react";
import { FeedbackMessage } from "../ui-helpers";
import { MatchExerciseContent } from "../../../../types/newtypes";
import BubbleBackground from "../../../common/BubbleBackground";
import { useMatchExercisePreview } from "./hooks/useMatchExercisePreview";
import { DraggableItem, DropZone } from "../ui-helpers";
import { useLessonDndSensors } from "./hooks/useLessonDndSensors";
import { useLessonDndAutoScroll } from "./hooks/useLessonDndAutoScroll";

interface MatchExercisePreviewProps {
  content: MatchExerciseContent;
  onCompleted?: () => void;
  // eslint-disable-next-line no-unused-vars
  onCardStateChange?: (...args: [boolean, boolean]) => void;
  cardId: string;
}

const MatchExercisePreview: React.FC<MatchExercisePreviewProps> = ({
  content,
  onCardStateChange,
  cardId,
}) => {
  const {
    activeId,
    allMatched,
    showInstruction,
    handleDragStart,
    handleDragEnd,
    getDraggableItems,
    getDropZones,
  } = useMatchExercisePreview({
    content,
    onCardStateChange,
    cardId,
  });

  const sensors = useLessonDndSensors();
  const { startLessonDndAutoScroll, stopLessonDndAutoScroll } = useLessonDndAutoScroll();

  return (
    <div className="w-full mb-4">
      {/* Header with Gamified Message */}
      <div className="mb-8">
        <Typography variant="h4" weight="bold" className="mb-4">
          {content?.slide_title}
        </Typography>

        <div className="mb-6">
          <MarkdownWrapper content={content?.intro_text || ""} />
        </div>

        {/* Gamified Instruction - Centered and Auto-hiding */}
        {showInstruction && (
          <div className="flex justify-center mb-8">
            <div className="absolute top-0 right-0">
              <BubbleBackground
                title="🎯 Drag & Match Challenge!"
                subtitle="Grab the items and drop them in the right zones."
                bubbleSide="left"
                wrapperClassName="w-80 h-40"
                textContainerClassName="!justify-end"
              />
            </div>
          </div>
        )}
      </div>

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        autoScroll={false}
        onDragStart={(event) => { startLessonDndAutoScroll(); handleDragStart(event); }}
        onDragEnd={(event) => { stopLessonDndAutoScroll(); handleDragEnd(event); }}
        onDragCancel={stopLessonDndAutoScroll}
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
          {/* Left Column - Draggable Items */}
          <div className="p-6 rounded-2xl border border-gray-200">
            <div className="flex items-center gap-2 mb-6">
              <GripVertical className="w-5 h-5 text-blue-600" />
              <Typography
                variant="h6"
                weight="bold"
                className="max-md:!text-base"
              >
                🎯 Items to Match
              </Typography>
            </div>
            <div
              data-lesson-dnd-scroll-container
              data-testid="dnd-match-items-scroll-container"
              className="h-[400px] max-md:max-h-[50vh] overflow-y-auto bg-gray-50 p-4 rounded-lg"
            >
              <SortableContext
                items={getDraggableItems()?.map((pair) => pair?.item)}
                strategy={verticalListSortingStrategy}
              >
                {getDraggableItems()?.map((pair) => (
                  <DraggableItem
                    key={pair?.item}
                    id={pair?.item}
                    item={pair?.item}
                    isMatched={false}
                  />
                ))}
              </SortableContext>
            </div>
          </div>

          {/* Right Column - Drop Zones */}
          <div className="bg-white p-6 rounded-2xl border border-gray-200">
            <div className="flex items-center gap-2 mb-6">
              <Box className="w-5 h-5 text-green-400" />
              <Typography
                variant="h6"
                weight="bold"
                className="max-md:!text-base"
              >
                Drop Zones
              </Typography>
            </div>
            <div
              data-lesson-dnd-scroll-container
              data-testid="dnd-match-drop-scroll-container"
              className="h-[400px] max-md:max-h-[50vh] overflow-y-auto"
            >
              <SortableContext
                items={getDropZones()?.map((zone) => zone?.id)}
                strategy={verticalListSortingStrategy}
              >
                {getDropZones()?.map((zone) => (
                  <DropZone
                    key={zone?.id}
                    id={zone?.id}
                    label={zone?.label}
                    matchedItem={zone?.matchedItem}
                    isCorrect={zone?.isCorrect}
                    isIncorrect={zone?.isIncorrect}
                    isLocked={zone?.isLocked}
                    onDrop={() => {}} // Handled in handleDragEnd
                    feedback={content?.feedback}
                    correctFeedback={zone?.correctFeedback}
                  />
                ))}
              </SortableContext>
            </div>
          </div>
        </div>

        <DragOverlay>
          {activeId ? (
            <div className="bg-gradient-to-r from-blue-400 to-blue-500 text-white px-6 py-4 rounded-xl shadow-2xl transform rotate-2">
              <div className="flex items-center gap-3">
                <GripVertical className="w-5 h-5 text-blue-200" />
                <Typography variant="p" className="text-white font-medium">
                  {activeId}
                </Typography>
              </div>
            </div>
          ) : null}
        </DragOverlay>
      </DndContext>

      {/* Completion Message */}
      {allMatched && (
        <FeedbackMessage
          isSuccess={true}
          title={content?.feedback?.correct_title || "Well done!"}
          message={content?.feedback?.correct_text || ""}
          className="mt-8"
        />
      )}
    </div>
  );
};

export default MatchExercisePreview;
